// @flow

import React from 'react';
import classNames from 'classnames';
import 'intersection-observer';
import VisibilityObserver from 'react-intersection-observer';

import { minute } from 'metrick/duration';

import { getDateString, getRelativeDateString, type DateLike } from 'app/lib/date';

import UserSessionStore, { type UserSessionEvent } from 'app/stores/UserSessionStore';

type Props = {|
  value: DateLike,
  updateFrequency: number,
  capitalized: boolean,
  seconds: boolean,
  fractionalSeconds: boolean,
  tabularNumerals: boolean,
  className?: string,
  clickable: boolean
|};

type State = {|
  text: string,
  utc: boolean
|};

const USER_SESSION_STORE_TIMESTAMP_UTC_KEY = "timestamps-utc";

/**
 * Renders a friendly, human readable, self-updating representation of a timestamp,
 * as supplied via `value` and stored in `text`.
 */
export default class FriendlyTime extends React.PureComponent<Props, State> {
  static defaultProps = {
    updateFrequency: minute.bind(1),
    capitalized: true,
    seconds: false,
    fractionalSeconds: false,
    tabularNumerals: false,
    clickable: true
  };

  _timeout: TimeoutID;

  constructor(props: Props) {
    super(props);

    this.state = {
      text: '',
      utc: UserSessionStore.get(USER_SESSION_STORE_TIMESTAMP_UTC_KEY) === "true"
    };

    // NOTE: This is separate because it relies on the value of `this.state.utc`!
    this.state.text = this.dateString;
  }

  get dateString() {
    const { value, capitalized, seconds, fractionalSeconds } = this.props;

    return (
      this.state.utc
        ? getDateString(value, { utc: true })
        : getRelativeDateString(value, { capitalized, seconds, fractionalSeconds })
    );
  }

  tick() {
    this.setState({ text: this.dateString }, this.maybeScheduleTick);
  }

  componentDidMount() {
    this.maybeScheduleTick();
    UserSessionStore.on('change', this.handleSessionDataChange);
  }

  maybeScheduleTick() {
    const { updateFrequency } = this.props;

    if (typeof updateFrequency == 'number' && updateFrequency > 0) {
      this._timeout = setTimeout(() => {
        this.tick();
      }, updateFrequency);
    }
  }

  maybeCancelTick() {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
  }

  componentWillUnmount() {
    this.maybeCancelTick();
    UserSessionStore.off('change', this.handleSessionDataChange);
  }

  componentDidUpdate({ updateFrequency }: Props, { utc }: State) {
    if (utc !== this.state.utc) {
      this.maybeCancelTick();
      this.tick();
    } else if (updateFrequency !== this.props.updateFrequency) {
      this.maybeCancelTick();
      this.maybeScheduleTick();
    }
  }

  handleVisibilityChange = (visible: boolean) => {
    this.maybeCancelTick();

    if (visible) {
      this.tick();
    }
  };

  handleSessionDataChange = ({ key, newValue }: UserSessionEvent) => {
    if (key === USER_SESSION_STORE_TIMESTAMP_UTC_KEY) {
      this.setState({ utc: newValue === 'true' });
    }
  };

  handleTimestampClick = (event: SyntheticMouseEvent<HTMLTimeElement>) => {
    event.preventDefault();

    const utc = !this.state.utc;
    this.setState({ utc });

    // Store it so next time we default to that choice
    UserSessionStore.set(USER_SESSION_STORE_TIMESTAMP_UTC_KEY, utc.toString());
  };

  render() {
    const className = classNames(
      this.props.className,
      {
        // Show a pointer cursor if the component accepts clicks
        "cursor-pointer": this.props.clickable
      }
    );

    let title = getDateString(this.props.value, { withSeconds: true, utc: this.state.utc });

    if (this.props.clickable) {
      // Append to the tooltip if the component accepts clicks
      title += '\n(Click to toggle between local and UTC)';
    }

    return (
      <VisibilityObserver
        tag="time"
        className={className}
        dateTime={this.props.value}
        title={title}
        onChange={this.handleVisibilityChange}
        onClick={this.props.clickable && this.handleTimestampClick}
      >
        {this.props.tabularNumerals ? (
          <span className="tabular-numerals">{this.state.text}</span>
        ) : this.state.text}
      </VisibilityObserver>
    );
  }
}
